import { useCallback, useEffect, useState } from "react";
import { useDependency } from "../../../../contexts/DependencyProvider";
import {
  ListStatusRequest,
  ListStatusType,
} from "../../../../services/soap/project/requests/ListStatusRequest";
import {
  Flex,
  Link,
  View,
  TextField,
  MenuTrigger,
  Menu,
  Item,
  ActionButton,
  Text,
} from "@adobe/react-spectrum";
import Edit from "@spectrum-icons/workflow/Edit";
import {
  AtollonIcon,
  PrintIcon,
  EditIcon,
  ToolIcon,
  TrashIcon,
  ActivityIconBlue,
} from "../../Icons/IconsLib";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { GetContextPathRequest } from "../../../../services/soap/project/requests/GetContextPathRequest";
import ButtonsComponent from "./ButtonsComponent";
import { GetActivityRequest } from "../../../../services/soap/project/requests/GetActivityRequest";
import { UpdateActivityRequest } from "../../../../services/soap/project/requests/UpdateActivityRequest";
import { CheckWorkFlowQueueRequest } from "../../../../services/soap/features/requests/CheckWorkFlowQueueRequest";
import { FPAData } from "../../../../infra/protected/FPA/FPAData";
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from "react-i18next";
import { AppDispatch } from "../../../../state/store";
import { useDispatch } from "react-redux";
import { setSelectedItem } from "../../../../state/Finder/finderSlice";
import styles from "./HeaderToolbarComponent.module.css";
import React from "react";
import { invokeFinderApp } from "../../../../utils/NavigationUtils";
import { getDisplayContextPathNodes } from "./contextPathUtils";

interface IActivityComponentProps {
  headerData: any;
  statusColor: (color: string) => string;
  onPrint?: (item: FPAData) => void;
  onEdit?: (item: FPAData) => void;
  onDelete?: (item: FPAData) => void;
  onTool?: (item: FPAData) => void;
  onAI?: (item: FPAData) => void;
  onCopy?: (item: FPAData) => void;
  onMove?: (item: FPAData) => void;
  selectedItem?: FPAData;
}

const ActivityComponent = ({
  headerData,
  statusColor,
  onPrint,
  onEdit,
  onDelete,
  onTool,
  onAI,
  selectedItem,
  onCopy,
  onMove,
}: IActivityComponentProps) => {
  const { store, projectService, featureService } = useDependency();
  const [activityStatusList, setActivityStatusList] = useState<any>([]);

  let [activityStatus, setActivityStatus] = useState("");
  const [fullPath, setFullPath] = useState<React.ReactNode>(null);
  const [activityData, setActivityData] = useState<any>([]);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRefId, setIsEditingRefId] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedRefId, setEditedRefId] = useState("");

  // Local state to track updated values
  const [displayName, setDisplayName] = useState<string>("");
  const [displayRefId, setDisplayRefId] = useState<string>("");

  // State to control menu open/close for double-click support
  const [isNameMenuOpen, setIsNameMenuOpen] = useState(false);
  const [isRefIdMenuOpen, setIsRefIdMenuOpen] = useState(false);

  // Update display values when headerData changes
  useEffect(() => {
    setDisplayName(headerData?.name || "");
    setDisplayRefId(headerData?.refId || "");
  }, [headerData?.name, headerData?.refId]);

  useEffect(() => {
    (async () => {
      headerData && (await getFullPath());
      await getActivityData();
      await getActivityStatusArray();
      setActivityStatus(headerData?.customState);
    })();
  }, [headerData?.id]);

  const getActivityStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listActivityStatus2(
      new ListStatusRequest(
        store.Server,
        store.SessionId,
        ListStatusType.Activity,
      ),
    );

    if (res.result === "OK" && res.STATUS.length > 0) {
      res?.STATUS.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color, TYPE } = status;
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color, types: TYPE });
      });
      //console.log('statusArray', statusArray);
      var filtered_status = statusArray.filter((status: any) =>
        status.types.find((type: any) => type.id === headerData?.activityType),
      );
      setActivityStatusList(filtered_status);
    }
    return statusArray;
  }, []);

  const getFullPath = async () => {
    const response = await projectService.getContextPath(
      new GetContextPathRequest(
        store.Server,
        store.SessionId,
        headerData?.id || 0,
      ),
    );
    const modifiedList = getDisplayContextPathNodes(
      response.LIST,
      headerData?.id,
      false,
    );
    //setFullPath(modifiedList.map(item => item.CONTEXT.name).join(' > '));
    const breadcrumbLinks = modifiedList.map((item, index) => (
      <div
        key={item.id}
        title={item.name}
        style={{ display: "inline" }}
      >
        <Link
          isQuiet
          UNSAFE_className={styles.folder_name_link}
          onPress={() => invokeFinderApp(String(item.id))}
        >
          {item.name && item.name.length > 90
            ? item.name.substring(0, 90) + "..."
            : item.name}
        </Link>
        {index < modifiedList.length - 1 && (
          <span style={{ color: "#9CA3AF", margin: "0 4px" }}> &gt; </span>
        )}
      </div>
    ));

    setFullPath(breadcrumbLinks);
  };

  function itemTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5}>{data.name}</View>
      </Flex>
    );
  }

  function valueTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5} UNSAFE_style={{ marginLeft: "10px" }}>
          <span title={data?.name}>
            {data?.name && data?.name.length > 16
              ? data?.name.substring(0, 16) + "..."
              : data?.name}
          </span>
        </View>
      </Flex>
    );
  }

  async function getActivityData() {
    if (selectedItem) {
      let { ACTIVITY } = await projectService.getActivity(
        new GetActivityRequest(store.Server, store.SessionId, selectedItem.id),
      );
      setActivityData(ACTIVITY);
    }
  }

  async function updateActivityStatus(e: any) {
    let resActivity = await projectService.updateActivity(
      new UpdateActivityRequest(store.Server, store.SessionId, {
        ...activityData,
        comment: selectedItem?.comment ?? activityData?.comment,
        customState: e.value,
      }),
    );
    let status = activityStatusList.find(
      (status: any) => status.id === e.value,
    );
    if (resActivity.result === "OK") {
      await featureService.checkWorkFlowQueue(
        new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
      );
      ToastQueue.positive(
        t("status_updated_successfully", { ns: "layout_components" }),
        { timeout: 1000 },
      );
      dispatch(
        setSelectedItem({
          item: selectedItem
            ? {
                ...selectedItem,
                statusName: status.name,
                statusColor: statusColor(status.color),
              }
            : null,
          show_context: false,
        }),
      );
    } else {
      ToastQueue.negative(resActivity.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  const copyUrl = () => {
    const url = `${window.location.origin}/finder?id=${headerData?.id}`;
    navigator.clipboard.writeText(url);
    ToastQueue.positive(
      t("link_copied_to_clipboard", { ns: "layout_components" }),
      { timeout: 1000 },
    );
  };

  // Handler to start editing activity name
  const handleEditName = () => {
    setEditedName(displayName || "");
    setIsEditingName(true);
    setIsNameMenuOpen(false);
  };

  // Handler to start editing refId
  const handleEditRefId = () => {
    setEditedRefId(displayRefId || "");
    setIsEditingRefId(true);
    setIsRefIdMenuOpen(false);
  };

  // Handler to save activity name
  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === displayName) {
      setIsEditingName(false);
      return;
    }

    try {
      let resActivity = await projectService.updateActivity(
        new UpdateActivityRequest(store.Server, store.SessionId, {
          ...activityData,
          name: editedName,
        }),
      );

      if (resActivity.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("activity_name_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Activity name updated successfully",
          }),
          {
          timeout: 1000,
          },
        );
        // Update local display state
        setDisplayName(editedName);
        dispatch(
          setSelectedItem({
            item: selectedItem
              ? {
                  ...selectedItem,
                  title: editedName,
                }
              : null,
            show_context: false,
          }),
        );
        // Refresh activity data
        await getActivityData();
      } else {
        ToastQueue.negative(resActivity.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_activity", {
            ns: "layout_components",
            defaultValue: "Failed to update activity",
          }),
        {
        timeout: 1000,
        },
      );
    }
    setIsEditingName(false);
  };

  // Handler to save refId
  const handleSaveRefId = async () => {
    if (editedRefId === displayRefId) {
      setIsEditingRefId(false);
      return;
    }

    try {
      let resActivity = await projectService.updateActivity(
        new UpdateActivityRequest(store.Server, store.SessionId, {
          ...activityData,
          refId: editedRefId,
        }),
      );

      if (resActivity.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("refid_updated_successfully", {
            ns: "layout_components",
            defaultValue: "RefId updated successfully",
          }),
          { timeout: 1000 },
        );
        // Update local display state
        setDisplayRefId(editedRefId);
        // Refresh activity data
        await getActivityData();
      } else {
        ToastQueue.negative(resActivity.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_activity", {
            ns: "layout_components",
            defaultValue: "Failed to update activity",
          }),
        {
        timeout: 1000,
        },
      );
    }
    setIsEditingRefId(false);
  };

  // Handler to cancel editing
  const handleCancelEdit = (type: "name" | "refId") => {
    if (type === "name") {
      setIsEditingName(false);
      setEditedName("");
    } else {
      setIsEditingRefId(false);
      setEditedRefId("");
    }
  };

  return (
    <Flex width="100%">
      <View UNSAFE_className={styles.header_main}>
        <View UNSAFE_className={styles.header_top_row}>
        <View UNSAFE_className={styles.header_left_column}>
        <View UNSAFE_className={styles.header_child}>
          <View UNSAFE_className={styles.frame_parent}>
            <View UNSAFE_className={styles.icon_activity_parent}>
              <ActivityIconBlue size={25} />
              {isEditingName ? (
                <TextField
                  value={editedName}
                  onChange={setEditedName}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") handleCancelEdit("name");
                  }}
                  width="250px"
                  autoFocus
                  isQuiet
                />
              ) : (
                <MenuTrigger
                  trigger="longPress"
                  isOpen={isNameMenuOpen}
                  onOpenChange={setIsNameMenuOpen}
                >
                  <div onDoubleClick={() => setIsNameMenuOpen(true)}>
                    <ActionButton
                      isQuiet
                      UNSAFE_style={{
                        padding: 0,
                        minWidth: "auto",
                        background: "transparent",
                        border: "none",
                        cursor: "text",
                        height: "auto",
                        fontWeight: "bold",
                      }}
                      UNSAFE_className={styles.activity_name}
                    >
                      <span
                        style={{ fontSize: "20px", padding: "5px" }}
                        title={displayName}
                      >
                        {displayName?.length > 45
                          ? displayName.substring(0, 45) + "..."
                          : displayName}
                      </span>
                    </ActionButton>
                  </div>
                  <Menu onAction={(key) => key === "edit" && handleEditName()}>
                    <Item key="edit">
                      <Edit size="S" />
                      <Text>
                        {t("edit_name", {
                          ns: "layout_components",
                          defaultValue: "Edit Name",
                        })}
                      </Text>
                    </Item>
                  </Menu>
                </MenuTrigger>
              )}
            </View>

            <View UNSAFE_className={styles.folder_name_parent}>
              <View
                UNSAFE_className={styles.folder_name}
                UNSAFE_style={{ wordBreak: "break-all" }}
              >
                {/* {fullPath.length > 90 ? fullPath.substring(0, 90) + '...' : fullPath} */}
                {fullPath}
              </View>
            </View>
          </View>
          <View UNSAFE_className={styles.tag_parent} marginTop={20}>
            <View UNSAFE_className={styles.tag}>
              <View UNSAFE_className={styles.activity_type}>
                <View UNSAFE_className={styles.activity_type_text}>
                  {headerData?.activityTypeName}
                </View>

                {isEditingRefId ? (
                  <>
                    <View UNSAFE_className={styles.divider}></View>
                    <TextField
                      value={editedRefId}
                      onChange={setEditedRefId}
                      onBlur={handleSaveRefId}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveRefId();
                        if (e.key === "Escape") handleCancelEdit("refId");
                      }}
                      width="150px"
                      autoFocus
                      isQuiet
                    />
                  </>
                ) : displayRefId ? (
                  <>
                    <View
                      UNSAFE_className={styles.divider}
                      UNSAFE_style={{ backgroundColor: "#fff" }}
                    ></View>
                    <MenuTrigger
                      trigger="longPress"
                      isOpen={isRefIdMenuOpen}
                      onOpenChange={setIsRefIdMenuOpen}
                    >
                      <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                        <ActionButton
                          isQuiet
                          UNSAFE_style={{
                            padding: 0,
                            minWidth: "auto",
                            background: "transparent",
                            border: "none",
                            cursor: "text",
                            height: "auto",
                          }}
                        >
                          <span
                            style={{ fontSize: "12px", padding: "5px" }}
                            className={styles.ref_id}
                          >
                            {displayRefId}
                          </span>
                        </ActionButton>
                      </div>
                      <Menu
                        onAction={(key) => key === "edit" && handleEditRefId()}
                      >
                          <Item key="edit">
                            <Edit size="S" />
                            <Text>
                              {t("edit_refid", {
                                ns: "layout_components",
                                defaultValue: "Edit RefID",
                              })}
                            </Text>
                          </Item>
                      </Menu>
                    </MenuTrigger>
                  </>
                ) : (
                  <>
                    {/* <ActionButton
                      isQuiet
                      onPress={handleEditRefId}
                      UNSAFE_style={{
                        padding: 0,
                        minWidth: "6px",
                        width: "6px",
                        background: "transparent",
                        border: "none",
                        cursor: "text",
                        height: "100%",
                      }}
                      aria-label={t("add_refid", {
                        ns: "layout_components",
                        defaultValue: "Add RefID",
                      })}
                    /> */}
                    <View
                      UNSAFE_className={styles.divider}
                      UNSAFE_style={{ backgroundColor: "#fff" }}
                    ></View>
                    <MenuTrigger
                      trigger="longPress"
                      isOpen={isRefIdMenuOpen}
                      onOpenChange={setIsRefIdMenuOpen}
                    >
                      <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                        <ActionButton
                          isQuiet
                          UNSAFE_style={{
                            padding: 0,
                            minWidth: "auto",
                            background: "transparent",
                            border: "none",
                            cursor: "text",
                            height: "auto",
                          }}
                        >
                          <span
                            style={{ fontSize: "12px", padding: "5px" }}
                            className={styles.ref_id}
                          >
                            {t("add_refid", {
                              ns: "layout_components",
                              defaultValue: "Add RefID",
                            })}
                          </span>
                        </ActionButton>
                      </div>
                      <Menu
                        onAction={(key) => key === "edit" && handleEditRefId()}
                      >
                          <Item key="edit">
                            <Edit size="S" />
                            <Text>
                              {t("add_refid", {
                                ns: "layout_components",
                                defaultValue: "Add RefID",
                              })}
                            </Text>
                          </Item>
                      </Menu>
                    </MenuTrigger>
                  </>
                )}
              </View>
            </View>
          </View>
        </View>
        </View>
        <View UNSAFE_className={styles.header_right_column}>
        <View UNSAFE_className={styles.header_actions_row}>
          <View UNSAFE_className={styles.status_picker_wrap}>
            {activityStatusList.length > 0 && (
              <DropDownListComponent
                cssClass={styles.status_dropdown}
                width={"100%"}
                name="vacancyTypeReef"
                dataSource={activityStatusList}
                fields={{ text: "name", value: "id" }}
                showClearButton={true}
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                value={activityStatus}
                onChange={(e: any) => {
                  updateActivityStatus(e);
                }}
              />
            )}
          </View>
          <View UNSAFE_className={styles.actions_wrap}>
            <ButtonsComponent
              styles={styles}
              AtollonIcon={AtollonIcon}
              PrintIcon={PrintIcon}
              EditIcon={EditIcon}
              ToolIcon={ToolIcon}
              TrashIcon={TrashIcon}
              onPrint={onPrint}
              onEdit={onEdit}
              onDelete={onDelete}
              onTool={onTool}
              onAI={onAI}
              selectedItem={selectedItem}
              onCopy={onCopy}
              onMove={onMove}
              onCopyLink={copyUrl}
            />
          </View>
        </View>
        </View>
        </View>
      </View>
    </Flex>
  );
};

export default ActivityComponent;
